// frontend/src/services/api.ts
import axios from "axios";
import type {
  Artist,
  Client,
  QueueStats,
  Event,
  JoinQueueData,
  ClientStatusType,
  EventStatus,
  RegisterData,
  User,
  Organization,
} from "../types";
import { getBackendRoot, getBaseApiUrl } from "../lib/env";

const BASE_API_URL = getBaseApiUrl();

const apiClient = axios.create({
  baseURL: BASE_API_URL,
  withCredentials: false,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * 🆕 Utilitaire pour résoudre les URLs des assets (logos, images)
 * Si le chemin est relatif (/uploads/...), on ajoute l'origine du backend si nécessaire, 
 * en garantissant la présence correcte des slashs pour éviter les URL relatives qui cassent l'affichage.
 */
export const getAssetUrl = (path?: string) => {
  if (!path) return undefined;
  if (path.startsWith("http")) return path;
  
  const backendRoot = getBackendRoot();
    
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  // Si backendRoot est vide, le chemin absolu avec un leading slash fera le travail depuis l'URL locale 
  return `${backendRoot}${cleanPath}`;
};

// ✅ INTERCEPTEUR : injecte le token à CHAQUE requête depuis localStorage
// Cela élimine totalement les race conditions avec le state React
apiClient.interceptors.request.use((config) => {
  const path = `${config.baseURL ?? ""}${config.url ?? ""}`;
  const isPublicAuth =
    path.includes("/auth/login") || path.includes("/auth/register");
  const token = localStorage.getItem("token");
  if (isPublicAuth) {
    if (config.headers && typeof config.headers.delete === "function") {
      config.headers.delete("Authorization");
    } else {
      delete config.headers.Authorization;
    }
  } else if (token) {
    if (config.headers && typeof config.headers.set === "function") {
      config.headers.set("Authorization", `Bearer ${token}`);
    } else {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return config;
});

// Compatibilité avec le code existant : setAuthToken persiste le token dans localStorage
export const setAuthToken = (token: string | null) => {
  if (token) {
    localStorage.setItem("token", token);
  } else {
    localStorage.removeItem("token");
  }
};

// ========== AUTHENTIFICATION ==========

export const login = async (email: string, password: string) => {
  try {
    const { data } = await apiClient.post("/auth/login", { email, password });
    setAuthToken(data.token);
    return {
      user: data.user as User,
      organization: data.organization as Organization,
      token: data.token as string,
    };
  } catch (error: any) {
    console.error("Login error:", error.response?.data || error.message);
    throw error;
  }
};

export const register = async (registerData: RegisterData) => {
  try {
    const { data } = await apiClient.post("/auth/register", registerData);
    setAuthToken(data.token);
    return {
      user: data.user as User,
      organization: data.organization as Organization,
      token: data.token as string,
    };
  } catch (error: any) {
    console.error("Register error:", error.response?.data || error.message);
    throw error;
  }
};

export const getConfig = async () => {
  const { data } = await apiClient.get("/auth/config");
  return data;
};

export const getCurrentSession = async (): Promise<{
  user: User;
  organization: Organization;
}> => {
  const { data } = await apiClient.get("/auth/me");
  return {
    user: {
      id: data.id,
      name: data.name,
      email: data.email,
      role: data.role,
      organizationId: data.organizationId,
      createdAt: data.createdAt,
    },
    organization: data.organization,
  };
};

/** @deprecated Utiliser getCurrentSession */
export const getCurrentArtist = async (): Promise<Artist> => {
  const session = await getCurrentSession();
  return {
    id: session.organization.id,
    name: session.user.name,
    email: session.user.email,
    specialty: session.organization.specialty,
    isAvailable: session.organization.isAvailable,
    avgTimePerTattoo: session.organization.avgTimePerClient,
    avgTimePerClient: session.organization.avgTimePerClient,
    businessType: session.organization.businessType,
    phone: session.organization.phone,
    createdAt: session.organization.createdAt,
    updatedAt: session.organization.createdAt,
  };
};

// ========== EVENTS ==========

/**
 * Vérifier un code événement
 */
export const verifyEventCode = async (code: string): Promise<Event> => {
  const { data } = await apiClient.post("/events/verify-code", { code });
  return data;
};

/**
 * Récupérer un événement par ID
 */
export const getEvent = async (eventId: string): Promise<Event> => {
  const { data } = await apiClient.get(`/events/${eventId}`);
  return data;
};

/**
 * Récupérer tous les événements d'un artiste
 */
export const getEventsByOrganization = async (organizationId: string): Promise<Event[]> => {
  const { data } = await apiClient.get(`/events/organization/${organizationId}`);
  return data;
};

/** @deprecated Utiliser getEventsByOrganization */
export const getEventsByArtist = async (artistId: string): Promise<Event[]> => {
  return getEventsByOrganization(artistId);
};

/**
 * Créer un nouvel événement
 */
export const createEvent = async (eventData: {
  artistId: string;
  name: string;
  description?: string;
  startDate: string;
  endDate?: string;
  location?: string;
  maxClients?: number;
  avgTimePerClient?: number;
  status?: EventStatus;
  phoneRequired?: boolean; // 🆕 AJOUTÉ
}): Promise<Event> => {
  const { data } = await apiClient.post("/events", eventData);
  return data;
};

/**
 * Mettre à jour un événement
 */
export const updateEvent = async (
  eventId: string,
  eventData: {
    name?: string;
    description?: string;
    startDate?: string;
    endDate?: string;
    location?: string;
    maxClients?: number;
    avgTimePerClient?: number;
    status?: EventStatus;
    phoneRequired?: boolean; // 🆕 AJOUTÉ
  },
): Promise<Event> => {
  const { data } = await apiClient.put(`/events/${eventId}`, eventData);
  return data;
};

/**
 * Mettre à jour uniquement le statut d'un événement (raccourci)
 */
export const updateEventStatus = async (
  eventId: string,
  status: EventStatus,
): Promise<Event> => {
  return updateEvent(eventId, { status });
};

/**
 * Supprimer un événement
 */
export const deleteEvent = async (eventId: string): Promise<void> => {
  await apiClient.delete(`/events/${eventId}`);
};

/**
 * Récupérer les stats d'un événement
 */
export const getEventStats = async (eventId: string) => {
  const { data } = await apiClient.get(`/events/${eventId}/stats`);
  return data;
};

// ========== QUEUE ==========

/**
 * Rejoindre la file d'attente
 */
export const joinQueue = async (queueData: JoinQueueData): Promise<Client> => {
  const { data } = await apiClient.post("/queue/join", queueData);
  return data;
};

/**
 * Récupérer la file d'un artiste
 */
export const getQueue = async (artistId: string): Promise<Client[]> => {
  const { data } = await apiClient.get(`/queue/artists/${artistId}`);
  return data;
};

/**
 * Récupérer les stats de la file d'un artiste
 */
export const getQueueStats = async (artistId: string): Promise<QueueStats> => {
  const { data } = await apiClient.get(`/queue/artists/${artistId}/stats`);
  return data;
};

/**
 * Récupérer la position d'un client
 */
export const getClientPosition = async (clientId: string) => {
  const { data } = await apiClient.get(`/queue/clients/${clientId}/position`);
  return data;
};

/**
 * Mettre à jour le statut d'un client
 */
export const updateClientStatus = async (
  clientId: string,
  status: ClientStatusType,
): Promise<Client> => {
  const { data } = await apiClient.put(`/queue/clients/${clientId}/status`, {
    status,
  });
  return data;
};

/**
 * Retirer un client de la file
 */
export const removeClient = async (clientId: string): Promise<void> => {
  await apiClient.delete(`/queue/clients/${clientId}`);
};

export const adjustClientTime = async (
  clientId: string,
  adjustment: number,
): Promise<{
  success: boolean;
  client: Client;
  adjustment: number;
  previousTime: number;
  newEstimatedTime: number;
  affectedClients: number;
}> => {
  const { data } = await apiClient.patch(
    `/queue/clients/${clientId}/adjust-time`,
    { adjustment },
  );
  return data;
};

/**
 * 🆕 Ajuster la durée de prestation
 */
export const adjustClientDuration = async (clientId: string, estimatedDuration: number) => {
  const { data } = await apiClient.patch(`/queue/clients/${clientId}/adjust-duration`, {
    estimatedDuration
  });
  return data;
};

/**
 * 🆕 Terminer une session avec paiement
 */
export const completeClient = async (clientId: string, completeData: {
  amountPaid: number;
  paymentMethod: string;
  deliveryMethod: string;
  actualDuration: number;
}) => {
  const { data } = await apiClient.post(`/queue/clients/${clientId}/complete`, completeData);
  return data;
};

/**
 * 🆕 Récupérer l'historique
 */
export const getHistory = async (artistId: string, eventId?: string) => {
  const { data } = await apiClient.get('/queue/history', {
    params: { artistId, eventId }
  });
  return data;
};

/**
 * 🆕 Branding & Settings
 */
export const getBranding = async (artistId?: string) => {
  const url = artistId ? `/queue/branding/${artistId}` : '/queue/branding';
  const { data } = await apiClient.get(url);
  return data;
};

export const updateBranding = async (organizationId: string, brandingData: any) => {
  const { data } = await apiClient.post('/queue/branding', {
    organizationId,
    ...brandingData
  });
  return data;
};

/**
 * 🆕 API Keys & Technical Settings
 */
export const getIntegrations = async (artistId: string) => {
  const { data } = await apiClient.get(`/queue/integrations/${artistId}`);
  return data;
};

export const updateIntegrations = async (organizationId: string, integrationData: any) => {
  const { data } = await apiClient.post('/queue/integrations', {
    organizationId,
    ...integrationData
  });
  return data;
};

export const uploadLogo = async (organizationId: string, file: File) => {
  const formData = new FormData();
  formData.append("logo", file);
  formData.append("organizationId", organizationId);
  const { data } = await apiClient.post("/queue/branding/upload-logo", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return data.logoUrl;
};

/**
 * 🆕 Mettre à jour les paramètres de notifications d'un client
 */
export const updateNotificationSettings = async (
  clientId: string,
  settings: {
    pushEnabled?: boolean;
    emailEnabled?: boolean;
    whatsappEnabled?: boolean;
    language?: string; // 🆕 AJOUTÉ
  },
): Promise<{
  pushEnabled: boolean;
  emailEnabled: boolean;
  whatsappEnabled: boolean;
}> => {
  const { data } = await apiClient.patch(
    `/queue/clients/${clientId}/notifications`,
    settings,
  );
  return data;
};

// ========== PUSH NOTIFICATIONS ==========

/**
 * Récupérer la clé VAPID publique
 */
export const getVapidKey = async (): Promise<string> => {
  const { data } = await apiClient.get("/push/vapid-key");
  return data.publicKey;
};

/**
 * S'abonner aux notifications push
 */
export const subscribeToPush = async (
  clientId: string,
  subscription: any,
): Promise<void> => {
  await apiClient.post("/push/subscribe", { clientId, subscription });
};

/**
 * Se désabonner des notifications push
 */
export const unsubscribeFromPush = async (clientId: string): Promise<void> => {
  await apiClient.post("/push/unsubscribe", { clientId });
};

/**
 * Vérifier l'état des notifications d'un client
 */
export const getNotificationStatus = async (clientId: string) => {
  const { data } = await apiClient.get(`/push/status/${clientId}`);
  return data;
};

/**
 * 🆕 Renvoyer une facture (V2)
 */
export const resendInvoice = async (invoiceId: string, channel: string) => {
  const { data } = await apiClient.post(`/queue/invoices/${invoiceId}/resend`, { channel });
  return data;
};

/**
 * 🆕 Journal des envois (V2)
 */
export const getDeliveryLogs = async (clientId?: string) => {
  const { data } = await apiClient.get('/queue/delivery-logs', { params: { clientId } });
  return data;
};

/**
 * 🆕 Stats détaillées (V2)
 */
export const getEventDetailedStats = async (eventId: string) => {
  const { data } = await apiClient.get(`/queue/stats/events/${eventId}`);
  return data;
};

export interface StatsSummary {
  totalEvents: number;
  activeEvents: number;
  completedEvents: number;
  totalClientsServed: number;
  clientsWaiting: number;
}

export const getStatsSummary = async (): Promise<StatsSummary> => {
  const { data } = await apiClient.get("/queue/stats/summary");
  return data;
};

// ────────────────────────────────────────────
// 💳 Billing / Stripe
// ────────────────────────────────────────────

export interface BillingStatus {
  plan: "FREE" | "PRO";
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  hasActiveSubscription: boolean;
  limits: {
    maxActiveEvents: number | null;
    maxClientsPerEvent: number | null;
    whatsappEnabled: boolean;
    historyDays: number | null;
    customBranding: boolean;
    poweredByVisible: boolean;
  };
}

export const getBillingStatus = async (): Promise<BillingStatus> => {
  const { data } = await apiClient.get("/billing/status");
  return data;
};

export const createCheckoutSession = async (): Promise<{ url: string }> => {
  const { data } = await apiClient.post("/billing/checkout");
  return data;
};

export const createPortalSession = async (): Promise<{ url: string }> => {
  const { data } = await apiClient.post("/billing/portal");
  return data;
};

export default apiClient;
